import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import {
  X,
  Loader2,
  AlertCircle,
  CheckCircle,
  DollarSign,
  FileText,
  AlertTriangle,
} from 'lucide-react';

/**
 * CreditNoteModal - Modal for creating manual credit notes
 *
 * Props:
 * - isOpen: boolean - Whether modal is open
 * - onClose: function - Called when modal is closed
 * - organization: object - Pre-selected organization (optional)
 * - invoice: object - Pre-selected invoice (optional)
 * - onCreditNoteCreated: function - Called when credit note is created
 */
const CreditNoteModal = ({
  isOpen,
  onClose,
  organization = null,
  invoice = null,
  onCreditNoteCreated,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [result, setResult] = useState(null);

  // Form state
  const [organizationId, setOrganizationId] = useState(organization?.id || '');
  const [invoiceId, setInvoiceId] = useState(invoice?.id || '');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');

  // Organizations list for dropdown
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);

  // Load organizations if not pre-selected
  useEffect(() => {
    if (isOpen && !organization) {
      loadOrganizations();
    }
  }, [isOpen, organization]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setOrganizationId(organization?.id || '');
      setInvoiceId(invoice?.id || '');
      setAmount(invoice ? (invoice.total / 100).toFixed(2) : '');
      setReason('');
      setNotes('');
      setError('');
      setSuccess(false);
      setResult(null);
    }
  }, [isOpen, organization, invoice]);

  const loadOrganizations = async () => {
    try {
      setLoadingOrgs(true);
      const response = await axios.get(
        `${getApiBase()}/admin/billing/organizations`,
        {
          headers: getAuthHeaders(),
          params: { limit: 100 }
        }
      );
      // API returns nested format: { organizations: [{ organization: {...}, billing_profile: {...} }] }
      // Extract the organization objects from the nested structure
      const orgsData = response.data.organizations || [];
      const flatOrgs = orgsData.map(item => item.organization || item).filter(Boolean);
      setOrganizations(flatOrgs);
    } catch (err) {
      console.error('Error loading organizations:', err);
    } finally {
      setLoadingOrgs(false);
    }
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(num);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!organizationId) {
      setError('Please select an organization');
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (!reason.trim()) {
      setError('Please enter a reason');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const amountCents = Math.round(parseFloat(amount) * 100);

      const response = await axios.post(
        `${getApiBase()}/admin/billing/credit-notes`,
        {
          organization_id: organizationId,
          invoice_id: invoiceId || null,
          amount: amountCents,
          currency: 'USD',
          reason: reason.trim(),
          notes: notes.trim() || null,
        },
        { headers: getAuthHeaders() }
      );

      setResult(response.data);
      setSuccess(true);

      // Notify parent and close after delay
      setTimeout(() => {
        if (onCreditNoteCreated) {
          onCreditNoteCreated();
        }
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Error creating credit note:', err);
      setError(err.response?.data?.detail || 'Failed to create credit note');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Success state
  if (success && result) {
    const xeroSuccess = result.xero_status === 'created';
    const xeroFailed = result.xero_status === 'failed';
    const xeroSkipped = result.xero_status === 'skipped';

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Credit Note Created
          </h3>
          <p className="text-gray-600">
            The credit note for {formatCurrency(amount)} has been created.
          </p>

          {/* Xero Status */}
          {xeroSuccess && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-700">
                Credit note created in Xero successfully.
              </p>
            </div>
          )}
          {xeroFailed && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start justify-center">
                <AlertTriangle className="w-4 h-4 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                <div className="text-left">
                  <p className="text-sm text-red-700 font-medium">
                    Xero sync failed
                  </p>
                  <p className="text-xs text-red-600 mt-1">
                    {result.xero_error || 'Unknown error'}
                  </p>
                  <p className="text-xs text-red-600 mt-1">
                    The credit note was saved locally. You may need to create it manually in Xero.
                  </p>
                </div>
              </div>
            </div>
          )}
          {xeroSkipped && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start justify-center">
                <AlertTriangle className="w-4 h-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" />
                <div className="text-left">
                  <p className="text-sm text-amber-700">
                    {result.xero_error || 'Xero integration not configured'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70]">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Create Credit Note</h3>
            <p className="text-sm text-gray-500">
              Issue a credit note to an organization
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-800">
            <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Organization */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Organization *
            </label>
            {organization ? (
              <div className="px-3 py-2 bg-gray-100 rounded-md text-gray-900">
                {organization.name}
              </div>
            ) : (
              <select
                value={organizationId}
                onChange={(e) => setOrganizationId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={loadingOrgs}
              >
                <option value="">Select an organization</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Invoice (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Related Invoice (optional)
            </label>
            {invoice ? (
              <div className="px-3 py-2 bg-gray-100 rounded-md text-gray-900 flex items-center">
                <FileText className="w-4 h-4 mr-2 text-gray-500" />
                {invoice.invoice_number || invoice.id}
              </div>
            ) : (
              <input
                type="text"
                value={invoiceId}
                onChange={(e) => setInvoiceId(e.target.value)}
                placeholder="e.g. INV-0067"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            )}
            <p className="text-xs text-gray-500 mt-1">
              Enter invoice number (e.g. INV-0067) or leave blank for a standalone credit note
            </p>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Amount (USD) *
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <DollarSign className="w-4 h-4 text-gray-400" />
              </div>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason *
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a reason</option>
              <option value="Service adjustment">Service adjustment</option>
              <option value="Goodwill credit">Goodwill credit</option>
              <option value="Billing error correction">Billing error correction</option>
              <option value="Promotional discount">Promotional discount</option>
              <option value="Contract adjustment">Contract adjustment</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Additional Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Enter any additional details..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start">
              <AlertCircle className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-700">
                This will create a credit note in Xero. The credit can be applied
                against future invoices for this organization.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Create Credit Note
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreditNoteModal;
